import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight, Facebook, Linkedin, Instagram, Youtube } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const socials = [
  { Icon: Facebook, link: "https://facebook.com/brivo360" },
  { Icon: Linkedin, link: "https://linkedin.com/company/brivo360" },
  { Icon: Instagram, link: "https://instagram.com/brivo360" },
  { Icon: Youtube, link: "https://youtube.com/@brivo360" },
];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cols = [
  {
    title: "Product",
    links: [
      { label: "Sell Online", href: "/business/sell-online" },
      { label: "Collect Payments", href: "/business/collect-payments" },
      { label: "Manage Inventory", href: "/business/manage-inventory" },
      { label: "Manage Operations", href: "/business/manage-operations" },
      { label: "Automate Orders", href: "/business/automate-orders" },
      { label: "Business Records", href: "/business/create-records" },
    ],
  },
  {
    title: "Solutions",
    links: [
      { label: "Solopreneurs", href: "/serve/solopreneurs" },
      { label: "Medium Businesses", href: "/serve/medium" },
      { label: "Large Businesses", href: "/serve/large" },
      { label: "Enterprise", href: "/serve/enterprise" },
      { label: "Modules", href: "/modules" },
      { label: "Pricing", href: "/pricing" },
    ],
  },
  {
    title: "Partners",
    links: [
      { label: "Reseller Program", href: "/partners/reseller" },
      { label: "Referral Program", href: "/partners/referral" },
      { label: "Community", href: "/community" },
      { label: "Blog", href: "/blog" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us", href: "/about" },
      { label: "Support", href: "/support" },
      { label: "FAQs", href: "/faqs" },
    ],
  },
];

const LEGAL = [
  { label: "Terms & Conditions", href: "/legal/terms" },
  { label: "Refund Policy", href: "/legal/refund" },
  { label: "Privacy Policy", href: "/legal/privacy" },
  { label: "Data Privacy", href: "/legal/data-privacy" },
  { label: "Cookies", href: "/legal/cookies" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed! Check your inbox soon.");
      setEmail("");
    } catch (err) {
      toast.error("Subscription failed. Try again.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <footer data-testid="main-footer" className="bg-[#1B4332] text-white relative overflow-hidden mt-20">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#2D6A4F]/40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#52B788]/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-[#E8F5E9] flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M4 6h12a4 4 0 010 8H4V6z" stroke="#1B4332" strokeWidth="2.5" strokeLinejoin="round"/>
                  <path d="M4 14h14a4 4 0 010 8H4v-8z" stroke="#1B4332" strokeWidth="2.5" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="font-display font-extrabold text-3xl">Brivo360</span>
            </Link>
            <p className="mt-5 text-white/70 leading-relaxed max-w-sm">
              The all-in-one platform that helps modern businesses sell smarter, manage inventory effortlessly, and grow without limits.
            </p>

            <form onSubmit={subscribe} className="mt-8 flex gap-2 max-w-md">
              <input
                data-testid="newsletter-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-white/60 transition-colors"
              />
              <button
                data-testid="newsletter-submit"
                disabled={loading}
                className="px-5 py-3 rounded-xl bg-[#FFF9E6] text-black font-semibold hover:bg-white transition-colors disabled:opacity-60"
              >
                {loading ? "..." : "Subscribe"}
              </button>
            </form>

            <div className="flex gap-3 mt-8">
  {socials.map(({ Icon, link }, i) => (
    <a
      key={i}
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
    >
      <Icon size={18} />
    </a>
  ))}
</div>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            {cols.map((col) => (
              <div key={col.title}>
                <h4 className="font-display font-bold text-lg mb-5">{col.title}</h4>
                <ul className="space-y-3">
                  {col.links.map((l) => (
                    <li key={l.href}>
                      <Link to={l.href} className="text-white/70 hover:text-white transition-colors text-sm flex items-center gap-1 group">
                        {l.label}
                        <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/15 flex flex-col gap-5 lg:flex-row lg:justify-between lg:items-center">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/60">
            <span>© {new Date().getFullYear()} Brivo360. All rights reserved.</span>
            
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/60">
            {LEGAL.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`footer-legal-${l.label.toLowerCase().replace(/\s+&\s+/g, '-').replace(/\s+/g, '-')}`}
                className="hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
